import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { FluentProvider, webLightTheme, webDarkTheme } from '@fluentui/react-components';
import { App } from './taskpane/App';
import { HostUnsupported } from './taskpane/HostUnsupported';

// Office.onReady never resolves outside a host, so a plain browser visit would otherwise
// render nothing at all with no clue why. Racing it against a timeout keeps `npm run dev`
// usable in a normal tab.
function whenOfficeReady(timeoutMs = 3000): Promise<string | null> {
  if (typeof Office === 'undefined' || typeof Office.onReady !== 'function') {
    return Promise.resolve(null);
  }
  return Promise.race([
    Office.onReady().then((info) => (info.host == null ? null : String(info.host))),
    new Promise<null>((resolve) => {
      setTimeout(() => resolve(null), timeoutMs);
    }),
  ]);
}

// Outlook reports its theme through officeTheme, but as colours rather than a light/dark
// flag, so the OS preference is the more reliable signal for picking a Fluent theme.
function preferDark(): boolean {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
}

async function bootstrap(): Promise<void> {
  const container = document.getElementById('root');
  if (!container) throw new Error('#root missing from taskpane.html');

  const host = await whenOfficeReady();
  const inOutlook = host === 'Outlook' && Office.context?.mailbox != null;

  createRoot(container).render(
    <StrictMode>
      <FluentProvider theme={preferDark() ? webDarkTheme : webLightTheme}>
        {inOutlook ? <App /> : <HostUnsupported host={host} />}
      </FluentProvider>
    </StrictMode>,
  );
}

void bootstrap();
